import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import logo from '../images/logo.png';

const navItems = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'courses', label: 'Courses' },
  { id: 'contact', label: 'Contact' },
];

const Navbar = ({ activeSection, scrollToSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the mobile sheet on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const goToSection = (sectionId) => {
    setIsOpen(false);
    // Sections only exist on the home page - route there first if needed
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
      return;
    }
    scrollToSection(sectionId);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        isScrolled
          ? 'border-b border-surface-line bg-surface-base/85 backdrop-blur-md'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="container-page">
        <div className="flex h-16 items-center justify-between md:h-20">
          {/* Brand */}
          <button
            onClick={() => goToSection('home')}
            className="flex items-center gap-3 text-left"
            aria-label="AC Tech Institute - back to top"
          >
            <img src={logo} alt="" className="h-9 w-9 rounded-lg object-contain" />
            <span className="leading-tight">
              <span className="block text-sm font-semibold tracking-tight text-ink">AC Tech Institute</span>
              <span className="hidden text-[11px] uppercase tracking-[0.18em] text-ink-faint sm:block">
                Beanibazar, Sylhet
              </span>
            </span>
          </button>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => {
              const active = location.pathname === '/' && activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => goToSection(item.id)}
                  aria-current={active ? 'page' : undefined}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    active ? 'text-ink' : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute inset-x-4 -bottom-0.5 h-px origin-left bg-brand-500 transition-transform duration-200 ${
                      active ? 'scale-x-100' : 'scale-x-0'
                    }`}
                  />
                </button>
              );
            })}

            <button onClick={() => goToSection('contact')} className="btn-primary ml-4 px-4 py-2">
              Enroll Now
            </button>
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-surface-line text-ink-muted transition-colors hover:text-ink md:hidden"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
          >
            {isOpen ? <FaTimes className="h-4 w-4" /> : <FaBars className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      <div
        className={`overflow-hidden border-t border-surface-line bg-surface-raised transition-[max-height,opacity] duration-300 md:hidden ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="container-page flex flex-col py-4">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => goToSection(item.id)}
              className={`rounded-lg px-3 py-3 text-left text-base font-medium transition-colors ${
                location.pathname === '/' && activeSection === item.id
                  ? 'bg-white/5 text-ink'
                  : 'text-ink-muted hover:bg-white/5 hover:text-ink'
              }`}
            >
              {item.label}
            </button>
          ))}
          <button onClick={() => goToSection('contact')} className="btn-primary mt-3">
            Enroll Now
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
